import * as React from "react";
import { documentService } from "../../services/documentService";
import { documentEditingService } from "../../services/documentEditingService";
import { bestaetigen } from "../redesign/bestaetigen";
import { nurDateiname } from "./fassungsListe";
import { PdfFileViewer } from "./PdfFileViewer";
import { ZOOM_START, zoomGroesser, zoomKleiner } from "./pdf-editor/zoom";
import { PdfEditorViewer, type EditorSteuerung } from "./PdfEditorViewer";
import { imEditorTabOeffnen } from "./pdf-editor/editorTabOeffnen";
import { DocumentVersionsDialog } from "./DocumentVersionsDialog";
import { DateinameKasten } from "./DateinameKasten";
import { KommentarEbene } from "./pdf-editor/KommentarEbene";
import { KommentareOhneStelle, KommentareUmschalter, KommentarWerkzeugKnopf } from "./pdf-editor/KommentarKnoepfe";
import { useDokumentKommentare } from "./pdf-editor/useDokumentKommentare";
import { useFassungsHinweis } from "./pdf-editor/useFassungsHinweis";
import { Ansichtsleiste, SchnellKnopf, WerkzeugGruppe, WerkzeugKnopf } from "./pdf-editor/Werkzeugleiste";
import { PdfIcons } from "./pdf-editor/pdfIcons";
import { erkenneDateiart } from "./pdf-editor/bildFormat";
import type { BildSitzung } from "./pdf-editor/bildSitzung";
import { isPdfLike } from "./documentKind";
import { BildAnsicht } from "./BildAnsicht";
import "./pdf-editor/pdfEditor.css";

/**
 * PDF oder Bild eines KMVS-Dokuments: ansehen (Standard) oder bearbeiten.
 *
 * Bilder (User 04.10.2026) laufen durch dieselbe Ansicht und denselben Editor:
 * das Bild wird für die Sitzung im Browser in ein Einseiten-PDF gehüllt
 * (`pdf-editor/bildSitzung.ts`). Ob die Datei ein Bild ist, entscheidet die
 * Signatur der geladenen Bytes — nach „Als PDF speichern“ oder dem
 * Wiederherstellen einer Fassung stimmt der Name des Aufrufers nicht mehr.
 *
 * Zwei Ansichten mit Absicht — der Lese-Viewer bleibt bei `react-pdf`
 * (eingespielt, schnell, deckt die große Mehrheit der Aufrufe ab), der Editor
 * läuft auf dem vollen PDF.js-Viewer, weil nur der den `AnnotationEditorLayer`
 * mitbringt. Siehe Kopfkommentar in `PdfEditorViewer.tsx`.
 *
 * Getrennt von `FileViewerContent`, weil Bearbeiten eine Dokument-Id braucht:
 * jener rendert auch Blobs ohne Id (z. B. Mail-Anhänge im Graph-Fenster).
 */

/** `name` mit der Endung von `vorbild` — eine frühere Fassung kann ein anderes Format haben als das Dokument heute. */
function mitEndungVon(name: string, vorbild: string): string {
    const endung = (n: string) => {
        const basis = nurDateiname(n);
        const punkt = basis.lastIndexOf(".");
        return punkt > 0 ? basis.slice(punkt) : "";
    };
    const soll = endung(vorbild);
    const ist = endung(name);
    if (!soll || soll.toLowerCase() === ist.toLowerCase()) return name;
    return `${ist ? name.slice(0, name.length - ist.length) : name}${soll}`;
}

/** Die ersten Bytes der Datei — `null`, wenn sie sich nicht lesen lassen (dann gilt sie als PDF). */
async function kopfVon(blob: Blob): Promise<Uint8Array | null> {
    try {
        return new Uint8Array(await blob.slice(0, 1024).arrayBuffer());
    } catch {
        return null;
    }
}

/**
 * Geht der Blob an pdf.js? Nur, wenn die Bytes ein PDF sind — oder wenn sie
 * unbekannt sind und Name/MIME-Typ ein PDF versprechen (pdf.js duldet Vorspann,
 * den die Signaturprüfung nicht kennt). Ein Bild, dessen Bytes sich nicht
 * erkennen lassen (beschädigt, HEIC unter `.jpg` …), geht NICHT an pdf.js —
 * dort erschiene nur „Failed to load PDF file“ (22.10.2026).
 */
export function ansichtFuerBytes(
    art: ReturnType<typeof erkenneDateiart> | "unlesbar",
    fileName: string,
    mimeType?: string | null,
): "pdf" | "bild-huelle" | "bild-roh" {
    if (art === "pdf") return "pdf";
    if (art && art !== "unlesbar") return "bild-huelle";
    return isPdfLike(fileName, mimeType) ? "pdf" : "bild-roh";
}

export const PdfDocumentView: React.FC<{
    docId: number;
    fileName: string;
    /** MIME-Typ des Dokuments — hilft, wenn die Bytes sich nicht erkennen lassen. */
    mimeType?: string | null;
    searchTerm?: string;
    /** Bearbeiten anbieten? Ohne Schreibrecht bleibt es beim Ansehen. */
    canEdit?: boolean;
    /** Nach dem Umbenennen — z. B. um den Fenstertitel nachzuziehen. */
    onRenamed?: (fileName: string) => void;
    /**
     * Läuft schon im eigenen Tab (`/dokumente/:id/pdf`) — dann gibt es „In neuem Tab“
     * nicht noch einmal.
     */
    imEigenenTab?: boolean;
    /** Direkt im Editor starten (`/dokumente/:id/pdf?bearbeiten=1`, User 02.10.2026). */
    startImEditor?: boolean;
    /**
     * Zu sehen? Im Editor-Tab mit mehreren Dokumenten (`PdfDokumentPage`) bleiben
     * die anderen eingehängt, damit ungespeicherte Arbeit den Tab-Wechsel übersteht —
     * sie dürfen dann aber auf keine Taste hören.
     */
    sichtbar?: boolean;
    /** Ungespeicherte Änderungen im Editor (Punkt am Dokument-Tab). */
    onAenderungen?: (ungespeichert: boolean) => void;
    /** Zugriff auf den laufenden Editor — `null`, solange nur angesehen wird. */
    steuerung?: React.MutableRefObject<EditorSteuerung | null>;
    /**
     * „Schließen“ im Editor. Liefert `true`, wenn der Aufrufer das selbst
     * erledigt hat (Dokument-Tab geschlossen) — sonst gilt das bisherige Verhalten.
     */
    onEditorVerlassen?: () => boolean;
}> = ({
    docId,
    fileName: fileNameProp,
    mimeType,
    searchTerm,
    canEdit = true,
    onRenamed,
    imEigenenTab = false,
    startImEditor = false,
    sichtbar = true,
    onAenderungen,
    steuerung,
    onEditorVerlassen,
}) => {
    // Was gerade angezeigt wird: die Object-URL (immer ein PDF) und — bei einem
    // Bild — die Sitzung mit dem Original, aus dem beim Speichern gerastert wird.
    const [geladen, setGeladen] = React.useState<{ url: string; bild: BildSitzung | null } | null>(null);
    const url = geladen?.url ?? null;
    const bild = geladen?.bild ?? null;
    // Kein PDF und keine erkennbare Bildsignatur, oder die Hülle ging schief:
    // dann die reine Bildvorschau statt pdf.js.
    const [rohBild, setRohBild] = React.useState<{ url: string; hinweis?: string } | null>(null);
    const [scale, setScale] = React.useState(ZOOM_START);
    const [error, setError] = React.useState<string | null>(null);
    const [editing, setEditing] = React.useState(startImEditor);
    const [versionsOpen, setVersionsOpen] = React.useState(false);
    // Eine FRÜHERE Fassung ist geöffnet (User 07.10.2026: „bei Ansehen im
    // KMVS-PDF diese Fassung anzeigen, und die kann ich dann nochmal
    // bearbeiten“). Angezeigt und bearbeitet wird dann deren Datei; erst beim
    // Speichern entscheidet sich, ob daraus die neueste Fassung wird.
    const [fruehere, setFruehere] = React.useState<{ versionNo: number; fileName: string } | null>(null);
    React.useEffect(() => setFruehere(null), [docId]);
    const fruehereNr = fruehere?.versionNo ?? null;
    // Hochzählen erzwingt einen frischen Blob nach dem Speichern — sonst zeigte
    // der Viewer weiter die alte Fassung aus dem Object-URL.
    const [reloadKey, setReloadKey] = React.useState(0);
    // Der Name kann sich hier ändern (Umbenennen, Speichern mit neuem Namen);
    // der Aufrufer kennt nur den beim Öffnen.
    const [fileName, setFileName] = React.useState(fileNameProp);
    React.useEffect(() => setFileName(fileNameProp), [fileNameProp]);
    // Name/Typ für die Weiche beim Laden — als Ref, damit Umbenennen nicht neu lädt.
    const typRef = React.useRef({ fileName: fileNameProp, mimeType });
    typRef.current = { fileName: fruehere?.fileName ?? fileName, mimeType: fruehere ? null : mimeType };
    const [aktionsFehler, setAktionsFehler] = React.useState<string | null>(null);
    // Kommentare liegen in KMVS, nicht in der Datei — Lese-Viewer und Editor
    // teilen sich denselben Stand. Kommentieren braucht keine neue Fassung.
    const kommentare = useDokumentKommentare(docId);
    const [kommentarWerkzeug, setKommentarWerkzeug] = React.useState(false);
    // Hinweise der Kommentar-Ebene (z. B. „Kommentar-Entwurf offen“).
    const [kommentarHinweis, setKommentarHinweis] = React.useState<string | null>(null);
    // Nach „Fassung wiederherstellen“ mit anderer Seitenzahl stehen Kommentare evtl. falsch.
    const fassung = useFassungsHinweis(kommentare.seitenzahl);
    const { setSichtbar: kommentareZeigen } = kommentare;
    const kommentarEntwurf = React.useCallback(() => {
        setKommentarWerkzeug(false);
        kommentareZeigen(true);
    }, [kommentareZeigen]);

    const umbenennen = React.useCallback(
        async (neuerName: string) => {
            setAktionsFehler(null);
            try {
                // Das Backend kann die Endung korrigieren — maßgeblich ist
                // der Name, den es zurückgibt.
                const doc = await documentService.update(docId, { fileName: neuerName });
                setFileName(doc.fileName);
                onRenamed?.(doc.fileName);
            } catch {
                setAktionsFehler("Umbenennen fehlgeschlagen.");
            }
        },
        [docId, onRenamed],
    );

    // KMVS selbst im neuen Tab, nur mit dem PDF (Route außerhalb des App-Rahmens,
    // `PdfDokumentPage`) — nicht die nackte Datei im Browser-Viewer: dort fehlten
    // Bearbeiten, Fassungen und Umbenennen. `window.open` synchron im Klick, sonst
    // hält der Popup-Blocker den Tab an.
    //
    // Seit 04.10.2026 über `oeffneImEditorTab`: ist schon ein Editor-Tab offen,
    // erscheint das Dokument dort als weiterer Tab, statt einen zweiten
    // Browser-Tab aufzumachen. Der Tab trägt einen eigenen Fensternamen (kein
    // `noopener` mehr) — nur so lässt er sich später nach vorn holen.
    const inNeuemTab = React.useCallback(() => void imEditorTabOeffnen(docId), [docId]);
    // User 02.10.2026: „neuen Tab öffnen, dann im neuen Tab KMVS-PDF öffnen" —
    // der Tab startet direkt im Editor.
    const imNeuemTabBearbeiten = React.useCallback(() => void imEditorTabOeffnen(docId, true), [docId]);
    // Im eigenen Tab, der aus KMVS heraus geöffnet wurde: Schließen schließt den
    // Tab. Sonst (Tab direkt aufgerufen) zurück zur Leseansicht.
    const editorSchliessen = React.useCallback(() => {
        if (onEditorVerlassen?.()) return;
        if (imEigenenTab && startImEditor && window.opener) {
            window.close();
            return;
        }
        setEditing(false);
    }, [imEigenenTab, onEditorVerlassen, startImEditor]);
    // `window.open` passiert im Service erst nach dem Holen der signierten
    // URL. Das bleibt innerhalb der kurzen Nutzeraktivierung, die Chromium/Edge
    // dem Klick gewähren — derselbe Weg wie in der Dokumentliste.
    const herunterladen = React.useCallback(() => {
        setAktionsFehler(null);
        if (fruehere) {
            // Eine frühere Fassung hat keinen signierten Link — als Datei aus dem Speicher.
            documentEditingService
                .fetchVersionBlob(docId, fruehere.versionNo)
                .then((blob) => {
                    const objectUrl = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = objectUrl;
                    a.download = `v${fruehere.versionNo}-${nurDateiname(fruehere.fileName)}`;
                    a.click();
                    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
                })
                .catch(() => setAktionsFehler("Herunterladen fehlgeschlagen."));
            return;
        }
        documentService.openSignedDownload(docId).catch(() => setAktionsFehler("Herunterladen fehlgeschlagen."));
    }, [docId, fruehere]);

    React.useEffect(() => {
        let alive = true;
        let objectUrl: string | null = null;
        setGeladen(null);
        setRohBild(null);
        setError(null);
        const name = typRef.current.fileName;
        const mime = typRef.current.mimeType;
        (fruehereNr != null ? documentEditingService.fetchVersionBlob(docId, fruehereNr) : documentService.fetchBlob(docId))
            .then(async (blob) => {
                const kopf = await kopfVon(blob);
                const art = kopf ? erkenneDateiart(kopf) : "unlesbar";
                const ansicht = ansichtFuerBytes(art, name, mime);
                if (ansicht !== "bild-huelle" || !art || art === "unlesbar" || art === "pdf") {
                    if (!alive) return;
                    objectUrl = URL.createObjectURL(blob);
                    if (ansicht === "pdf") setGeladen({ url: objectUrl, bild: null });
                    else setRohBild({ url: objectUrl });
                    return;
                }
                // Ein Bild: für Ansicht und Editor in ein Einseiten-PDF hüllen.
                // Erst hier geladen — PDFs brauchen das Modul nicht.
                try {
                    const { bildAlsPdf } = await import("./pdf-editor/bildSitzung");
                    const { pdf, sitzung } = await bildAlsPdf(blob, art);
                    if (!alive) return;
                    const kopie = new Uint8Array(pdf.byteLength);
                    kopie.set(pdf);
                    objectUrl = URL.createObjectURL(new Blob([kopie], { type: "application/pdf" }));
                    setGeladen({ url: objectUrl, bild: sitzung });
                } catch (err) {
                    // Zu groß, nicht dekodierbar …: ansehen geht trotzdem, nur ohne Editor.
                    if (!alive) return;
                    const grund = err instanceof Error && err.message ? `${err.message} ` : "";
                    objectUrl = URL.createObjectURL(blob);
                    setRohBild({ url: objectUrl, hinweis: `${grund}Das Bild lässt sich hier nur ansehen, nicht bearbeiten.` });
                }
            })
            .catch(() => alive && setError("Datei konnte nicht geladen werden."));
        return () => {
            alive = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [docId, reloadKey, fruehereNr]);

    const { merke: merkeSeitenzahl } = fassung;
    const onSaved = React.useCallback((ergebnis?: { neuesDokument?: { id: number; fileName: string } }) => {
        setEditing(false);
        // Nach dem Speichern gilt wieder der aktuelle Stand — ob er nun aus der
        // früheren Fassung hervorging oder unberührt blieb (eigenes Dokument).
        if (fruehereNr != null && !ergebnis?.neuesDokument) merkeSeitenzahl();
        setFruehere(null);
        setReloadKey((k) => k + 1);
        const neu = ergebnis?.neuesDokument;
        if (neu) {
            void bestaetigen({
                titel: "Als eigenes Dokument gespeichert",
                text: `„${nurDateiname(neu.fileName)}“ liegt jetzt neben dem bisherigen Dokument — am selben Kunden und im selben Vorgang. Das bisherige ist unverändert.`,
                ja: "Neues Dokument öffnen",
                nein: "Schließen",
            }).then((oeffnen) => {
                if (oeffnen) void imEditorTabOeffnen(neu.id);
            });
            return;
        }
        // Der Speichern-Dialog kann umbenennen — den neuen Namen nachladen.
        documentService
            .getOne(docId)
            .then((doc) => {
                setFileName(doc.fileName);
                onRenamed?.(doc.fileName);
            })
            .catch(() => undefined);
    }, [docId, fruehereNr, merkeSeitenzahl, onRenamed]);

    const zurAktuellen = React.useCallback(() => {
        setEditing(false);
        setFruehere(null);
    }, []);

    if (error) return <div className="pdfx pdfx-mitte" role="alert">{error}</div>;
    if (rohBild) {
        return (
            <BildAnsicht
                url={rohBild.url}
                fileName={fruehere ? nurDateiname(fruehere.fileName) : fileName}
                onDownload={herunterladen}
                hinweis={rohBild.hinweis ?? (aktionsFehler || undefined)}
            />
        );
    }
    if (!url) return <div className="pdfx pdfx-mitte" role="status">Wird geladen…</div>;

    // Genau eine Meldung unter den Knöpfen — Vorrang von oben nach unten.
    const meldung = aktionsFehler ? (
        <span className="pdfx-fehler" role="alert">{aktionsFehler}</span>
    ) : fruehere ? (
        <span className="pdfx-fassungshinweis" role="status">
            <span>
                Du siehst <strong>Fassung {fruehere.versionNo}</strong> — nicht den aktuellen Stand.
                {canEdit ? " „Bearbeiten“ arbeitet auf dieser Fassung weiter; beim Speichern wählst du, was daraus wird." : ""}
            </span>
            <button type="button" className="pdfx-kbtn" onClick={zurAktuellen}>
                Zur aktuellen Fassung
            </button>
        </span>
    ) : !bild && kommentare.fehler ? (
        <span className="pdfx-fehler" role="alert">{kommentare.fehler}</span>
    ) : !bild && (kommentarHinweis || fassung.hinweis) ? (
        <span role="status">{kommentarHinweis ?? fassung.hinweis}</span>
    ) : (searchTerm ?? "").trim() ? (
        <span role="status">Suche: „{(searchTerm ?? "").trim()}“</span>
    ) : null;

    if (editing) {
        return (
            <PdfEditorViewer
                key={`edit-${reloadKey}-${fruehereNr ?? "a"}`}
                docId={docId}
                url={url}
                // Der Name des Dokuments bleibt — nur die Endung folgt der Fassung.
                fileName={fruehere ? mitEndungVon(fileName, fruehere.fileName) : fileName}
                onSaved={onSaved}
                onCancel={editorSchliessen}
                // Kommentare hängen an PDF-Seiten — ein Bild, das gedreht wird, hat keine feste Stelle dafür.
                // Und sie gehören zum AKTUELLEN Stand, nicht zu einer früheren Fassung.
                kommentare={bild || fruehere ? undefined : kommentare}
                fassung={fruehere ? { versionNo: fruehere.versionNo } : null}
                bild={bild}
                sichtbar={sichtbar}
                onAenderungen={onAenderungen}
                steuerung={steuerung}
            />
        );
    }

    return (
        <div className="pdfx pdfx-root">
            <Ansichtsleiste
                schnell={
                    <>
                        <span className="pdfx-art" title={bild ? "Bild" : "PDF"}>
                            {bild ? PdfIcons.bild : PdfIcons.pdf}
                        </span>
                        <DateinameKasten
                            fileName={fruehere ? nurDateiname(fruehere.fileName) : fileName}
                            onRename={canEdit && !fruehere ? umbenennen : undefined}
                        />
                    </>
                }
                rechts={
                    canEdit && (
                        <SchnellKnopf
                            primaer
                            icon={PdfIcons.bearbeiten}
                            onClick={() => setEditing(true)}
                            title={fruehere ? `Fassung ${fruehere.versionNo} weiterbearbeiten` : `${fileName} bearbeiten`}
                        >
                            Bearbeiten
                        </SchnellKnopf>
                    )
                }
                hinweis={meldung}
            >
                <WerkzeugGruppe titel="Ansicht">
                    <WerkzeugKnopf icon={PdfIcons.zoomMinus} onClick={() => setScale(zoomKleiner)} label="Verkleinern" />
                    <span className="pdfx-zoomwert" aria-label="Zoom">{Math.round(scale * 100)} %</span>
                    <WerkzeugKnopf icon={PdfIcons.zoomPlus} onClick={() => setScale(zoomGroesser)} label="Vergrößern" />
                </WerkzeugGruppe>
                {!bild && !fruehere && (
                    <WerkzeugGruppe titel="Kommentare">
                        {canEdit && (
                            <KommentarWerkzeugKnopf
                                aktiv={kommentarWerkzeug}
                                onClick={() => setKommentarWerkzeug((a) => !a)}
                            />
                        )}
                        <KommentareUmschalter kommentare={kommentare} />
                        <KommentareOhneStelle kommentare={kommentare} />
                    </WerkzeugGruppe>
                )}
                <WerkzeugGruppe titel="Datei">
                    <WerkzeugKnopf
                        icon={PdfIcons.herunterladen}
                        onClick={herunterladen}
                        label="Herunterladen"
                        title={fruehere ? `Fassung ${fruehere.versionNo} herunterladen` : `${fileName} herunterladen`}
                    />
                    <WerkzeugKnopf
                        icon={PdfIcons.fassungen}
                        onClick={() => setVersionsOpen(true)}
                        label="Fassungen"
                        title="Alle Fassungen dieses Dokuments"
                    />
                </WerkzeugGruppe>
                {!imEigenenTab && !fruehere && (
                    <WerkzeugGruppe titel="Neuer Tab">
                        <WerkzeugKnopf
                            icon={PdfIcons.neuerTab}
                            onClick={inNeuemTab}
                            label="In neuem Tab"
                            title="Im KMVS-Editor-Tab öffnen — ohne Seitenleiste und Kopfzeile. Ist schon einer offen, kommt das Dokument dort als weiterer Tab dazu."
                        />
                        {canEdit && (
                            <WerkzeugKnopf
                                icon={PdfIcons.tabBearbeiten}
                                onClick={imNeuemTabBearbeiten}
                                label="Im neuen Tab bearbeiten"
                                title="Im KMVS-Editor-Tab direkt zum Bearbeiten öffnen. Ist schon einer offen, kommt das Dokument dort als weiterer Tab dazu."
                            />
                        )}
                    </WerkzeugGruppe>
                )}
            </Ansichtsleiste>
            <div className="pdfx-stage">
                <PdfFileViewer
                    key={`view-${reloadKey}-${fruehereNr ?? "a"}`}
                    url={url}
                    searchTerm={searchTerm}
                    scale={scale}
                    ladeText={bild ? "Bild wird geladen…" : undefined}
                    ebene={
                        bild || fruehere
                            ? undefined
                            : ({ geometrie, container }) => (
                                  <KommentarEbene
                                      geometrie={geometrie}
                                      container={container}
                                      kommentare={kommentare}
                                      werkzeugAktiv={kommentarWerkzeug}
                                      darfSchreiben={canEdit}
                                      onEntwurf={kommentarEntwurf}
                                      onHinweis={setKommentarHinweis}
                                  />
                              )
                    }
                />
            </div>
            <DocumentVersionsDialog
                docId={docId}
                open={versionsOpen}
                onOpenChange={setVersionsOpen}
                onRestored={() => {
                    fassung.merke();
                    onSaved();
                }}
                canRestore={canEdit}
                onAnsehen={(v) => {
                    setAktionsFehler(null);
                    setFruehere({ versionNo: v.versionNo, fileName: v.fileName });
                }}
            />
        </div>
    );
};
